import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Plus, Stethoscope, User, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch, useFieldArray } from "react-hook-form";
import { Trans, useTranslation } from 'react-i18next';

import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import { useModalEffects } from '@/shared/hooks/use-modal-effects';
import { usePatientDetail } from '@/shared/hooks/use-patient-actions';
import { useMyProfile } from '@/shared/hooks/use-my-profile';

import { AppUser, type PatientListItem } from '@/api/adapters/patients';

import {
  fetchDiseaseCodes,
  fetchDiseaseGroups,
  fetchStaff,
} from '@/api/adapters/patients';
import {
  GUARDIAN_RELATIONSHIPS,
  patientRegistrationSchema,
  type PatientRegistrationFormValues,
} from '@/features/patients/schemas/patient-registration.schema';
import { StaffSearchModal } from './StaffSearchModal';

// ── Stable "nothing loaded yet" values ──
// A data-fetching hook hands back `undefined` until its request resolves, so the usual
// `const { data: rows = [] } = useQuery(...)` writes a BRAND-NEW empty array on every render for as
// long as that is true. Any effect that lists such a value among its dependencies therefore sees a
// changed dependency on every render and re-runs every time. The reset effect below both depends on
// the staff list and calls a state setter, so each run scheduled the render that triggered the next
// run — an unbounded render loop that only ever stopped because the request came back. Hoisting the
// empty values to module level makes "nothing loaded yet" ONE value instead of a fresh one per
// render, which is what actually breaks the loop.
const NO_STAFF: Awaited<ReturnType<typeof fetchStaff>> = [];
const NO_DISEASE_GROUPS: Awaited<ReturnType<typeof fetchDiseaseGroups>> = [];
const NO_DISEASE_CODES: Awaited<ReturnType<typeof fetchDiseaseCodes>> = [];

// ── Staff type (matches existing app patterns) ──
interface StaffMember {
	id: number;
	email: string;
	name: string;
	department: string;
	role: string;
}

interface Props {
	open: boolean;
	/** "create" → register new patient; "edit" → update existing patient. */
	mode: 'create' | 'edit';
	/** Required in edit mode — the patient being edited. */
	patient?: PatientListItem | null;
	onClose: () => void;
	onSubmit: (values: PatientRegistrationFormValues) => void;
	isSubmitting?: boolean;
}

function formDefaults(p?: PatientListItem | null): PatientRegistrationFormValues {
	const patient = p ?? null;
	return {
		appUser: patient?.appUser ?? AppUser.PATIENT,
		name: patient?.name ?? '',
		mrn: patient?.mrn ?? '',
		dischargeDate: patient?.dischargeDate ?? '',
		primaryDx: patient?.primaryDxCode ?? '',
		secondaryDx: patient?.secondaryDx ?? '',
		// PRD 03 §3.2: 처방약 keeps at least one row visible ("최소 1행 유지"). Seed an empty
		// row into the reset baseline so it survives the reset-on-open effect's async re-fires
		// (SHCT-555). Empty rows are stripped at submit (see handleFormSubmit).
		medications: patient?.medications?.length
			? patient.medications
			: [{ name: '', freq: '' }],
		patientPhone: patient?.phone ?? '',
		birthDate: patient?.birthDate ?? '',
		gender: patient?.gender ?? '',
		guardianName: patient?.guardian?.name ?? '',
		guardianRelationship: patient?.guardian?.relationship ?? '',
		guardianPhone: patient?.guardian?.mobile ?? '',
	};
}

export function PatientFormModal({
	open,
	mode,
	patient,
	onClose,
	onSubmit,
	isSubmitting = false,
}: Props) {
	const { t } = useTranslation(['medical', 'common', 'validation']);
	const [showStaffSearch, setShowStaffSearch] = useState(false);
	const [staffSearch, setStaffSearch] = useState('');
	const [assignedStaff, setAssignedStaff] = useState<StaffMember[]>([]);

	const isEdit = mode === 'edit';

	// Current staff for auto-assignment (PRD §3.2: 본인 자동 주담당).
	const { data: myProfile } = useMyProfile(open);

	// Fetch full detail in edit mode so the form gets medication, guardianPhone, etc.
	// that are absent from the list. Enabled only when the modal is open to avoid
	// premature fetches. React Query cache (staleTime: 60s) makes reopens instant.
	// unmask=true: the edit form pre-fills the registration layout, and a masked
	// name/phone cannot be edited back to a real value, so it needs the unmasked
	// record (PRD 03 §3.3). Cached separately from the detail header's masked read.
	const { data: detail } = usePatientDetail(
		isEdit && open ? patient?.uid : undefined,
		true,
	);

	// Care-team staff selector (FEATURE_038) + disease masters (FEATURE_014) from the BE.
	// `enabled: open` for the same reason the detail read above is gated: the patient list keeps this
	// modal mounted at all times and merely flips `open`, so without the gate both master-data reads
	// fired on every visit to the list — for a form nobody had opened. The cache (see staleTime)
	// still makes the first open instant on any later visit.
	const { data: staffOptions = NO_STAFF } = useQuery({
		queryKey: ['staff'],
		queryFn: () => fetchStaff(),
		staleTime: 300_000,
		enabled: open,
	});
	const staffMembers: StaffMember[] = staffOptions.map((s) => ({
		id: s.id,
		email: s.email,
		name: s.name,
		department: s.department ?? '',
		role: s.role,
	}));
	const { data: domainOptions = NO_DISEASE_GROUPS } = useQuery({
		queryKey: ['disease', 'groups'],
		queryFn: fetchDiseaseGroups,
		staleTime: Infinity,
		enabled: open,
	});

	const {
		register,
		handleSubmit,
		control,
		setValue,
		reset,
		clearErrors,
		trigger,
		formState: { errors, isValid, isSubmitted, touchedFields },
	} = useForm<PatientRegistrationFormValues>({
		resolver: zodResolver(patientRegistrationSchema),
		mode: 'onTouched',
		defaultValues: formDefaults(),
	});

	// SHCT-840 — 편집 모달은 기존 값이 세팅된 채로 열린다. 그 값이 현재 규칙을 이미 위반하고 있으면
	// [수정] 버튼은 [공통] 00 §1.2("필수 필드 전체 통과 전까지 비활성")대로 비활성인데, 에러 문구는
	// 같은 조항의 "필드 이탈(blur) 시 노출" 때문에 한 줄도 뜨지 않는다 — 사용자가 건드린 필드가 없어서다.
	// 그래서 어디를 고쳐야 버튼이 살아나는지 알 수 없는 화면이 된다(QC: "수정이 일어나도 버튼이 활성화되지 않음").
	// 편집 모드로 열릴 때 한 번 검증해서, 이미 위반 중인 필드의 에러만 처음부터 보이게 한다. 버튼의
	// 활성 조건 자체는 §1.2 그대로 두고, 이유만 화면에 드러낸다. 신규 등록 모드에는 적용하지 않는다 —
	// 빈 폼을 열자마자 필수 에러가 전부 뜨는 것은 §1.2의 검증 시점에 어긋난다.
	const [revealPrefillErrors, setRevealPrefillErrors] = useState(false);
	const showAll = isSubmitted || revealPrefillErrors;

	const appUser = useWatch({ control, name: 'appUser' });
	const primaryDx = useWatch({ control, name: 'primaryDx' });
	const { fields: medFields, append: medAppend, remove: medRemove } =
		useFieldArray({ control, name: 'medications' });
	const prevAppUser = useRef<string | undefined>(undefined);

	const modalRef = useRef<HTMLDivElement>(null);

	useModalEffects({
		open,
		onClose,
		modalRef,
		hasSubModal: showStaffSearch,
		onCloseSubModal: () => setShowStaffSearch(false),
		// Don't auto-focus the first field: this form validates on blur (mode: 'onTouched'),
		// and an auto-focused required field gets blurred during the modal's mount churn,
		// firing validation before the user types (name error on open — SHCT bug #9).
		autoFocusFirstField: false,
	});

	// ── Clear stale validation errors when appUser type changes (create mode only) ──
	useEffect(() => {
		if (isEdit) return; // appUser is immutable in edit mode
		if (prevAppUser.current === undefined) {
			prevAppUser.current = appUser;
			return;
		}
		if (prevAppUser.current === appUser) return;
		prevAppUser.current = appUser;

		if (appUser === AppUser.PATIENT) {
			clearErrors([
				'birthDate',
				'gender',
				'guardianName',
				'guardianRelationship',
				'guardianPhone',
			]);
		} else {
			clearErrors('patientPhone');
		}
	}, [appUser, clearErrors, isEdit]);

	const isInitialReset = useRef(true);
	const subcodesReadyRef = useRef(false);

	// Reset secondaryDx when primaryDx changes — but skip the initial programmatic
	// set from formDefaults, otherwise it wipes the pre-populated secondaryDx.
	useEffect(() => {
		if (isInitialReset.current) {
			isInitialReset.current = false;
			return;
		}
		setValue('secondaryDx', '');
	}, [primaryDx, setValue]);

	// Reset/populate form on open/close. In edit mode the form is filled once the
	// detail fetch resolves; the patient prop provides uid for the query only.
	// Auto-assigns the logged-in staff as primary care team member (PRD §3.2).
	useEffect(() => {
		if (open) {
			if (isEdit && detail) {
				isInitialReset.current = true;
				subcodesReadyRef.current = false;
				reset(formDefaults(detail));
			} else if (!isEdit) {
				reset(formDefaults());
			}
			// Seed the care-team picker. Always include self as primary (PRD §3.2). In edit
			// mode also restore the patient's existing ASSISTING members from detail.careTeam
			// (their staff ids) so the picker shows the saved team, not just self (PRD §3.3).
			// Ids are resolved to full staff rows via the loaded staff list; an id with no
			// match (a staff row not in the list) is skipped rather than shown blank.
			// Register: the logged-in staff is the auto-assigned primary (PRD §3.2).
			// Edit: the primary is the patient's EXISTING primary attending (detail.primaryStaffId) —
			// NOT whoever is viewing, otherwise every viewer sees themselves as primary. Assisting
			// members come from detail.careTeam (assisting ids; the primary is excluded there).
			if (isEdit && detail) {
				// SHCT-954: match the primary attending by ID, never by name. Two providers can share a
				// name, and `staffOptions` holds ACTIVE staff only — so a Withdrawn (탈퇴) attending is
				// absent from it and matching by name picked a same-named ACTIVE provider instead,
				// showing that other person's 부서 beside the name.
				const primaryRow =
					detail.primaryStaffId != null
						? staffOptions.find((m) => m.id === detail.primaryStaffId)
						: undefined;
				// Not in the loaded staff list (a Withdrawn (탈퇴) or PENDING attending — the selector
				// excludes both): render from the response instead. The id is the REAL staff id, which
				// matters beyond display — the assisting filter below compares against it, so a genuine
				// assisting member is no longer dropped from the team on save. -1 remains only for an
				// unassigned patient, where there is no provider and so no id to carry.
				const primary: StaffMember = primaryRow
					? {
							id: primaryRow.id,
							email: primaryRow.email,
							name: primaryRow.name,
							department: primaryRow.department ?? '',
							role: primaryRow.role,
						}
					: {
							id: detail.primaryStaffId ?? -1,
							email: '',
							name: detail.primaryStaff ?? '',
							department: detail.primaryStaffDepartment ?? '',
							role: '',
						};
				const assisting = (detail.careTeam ?? [])
					.map((sid) => staffOptions.find((m) => String(m.id) === sid))
					.filter((m) => !!m && m.id !== primary.id)
					.map(
						(m): StaffMember => ({
							id: m!.id,
							email: m!.email,
							name: m!.name,
							department: m!.department ?? '',
							role: m!.role,
						}),
					);
				setAssignedStaff([primary, ...assisting]);
			} else if (myProfile) {
				setAssignedStaff([
					{
						id: myProfile.id,
						email: myProfile.email,
						name: myProfile.name,
						department: myProfile.department,
						role: myProfile.role,
					},
				]);
			} else {
				setAssignedStaff([]);
			}
		} else {
			reset(formDefaults());
			setAssignedStaff([]);
			setShowStaffSearch(false);
			setStaffSearch('');
			subcodesReadyRef.current = false;
		}
	}, [open, isEdit, detail, myProfile, staffOptions, reset]);

	// SHCT-840 — see the note on `revealPrefillErrors`. Runs after the reset above has seeded the form
	// with the fetched record, so `trigger()` judges the stored values, not the empty defaults. Only a
	// record that FAILS opens with its errors revealed; a clean record still behaves exactly as before
	// (nothing shown until the user leaves a field). The flag is cleared on close so the next open —
	// possibly a create — starts quiet again.
	useEffect(() => {
		if (!open || !isEdit || !detail) {
			if (!open) setRevealPrefillErrors(false);
			return;
		}
		let cancelled = false;
		void trigger().then((valid) => {
			if (!cancelled) setRevealPrefillErrors(!valid);
		});
		return () => {
			cancelled = true;
		};
	}, [open, isEdit, detail, trigger]);

	// Ensure at least one medication row is always visible.
	useEffect(() => {
		if (medFields.length === 0) medAppend({ name: '', freq: '' });
	}, [medFields.length, medAppend]);

	const { data: subcodes = NO_DISEASE_CODES } = useQuery({
		queryKey: ['disease', 'codes', primaryDx],
		queryFn: () => fetchDiseaseCodes(primaryDx),
		enabled: open && !!primaryDx,
		staleTime: 300_000,
	});

	useEffect(() => {
		if (isEdit && detail && subcodes.length > 0 && !subcodesReadyRef.current) {
			subcodesReadyRef.current = true;
			setValue('secondaryDx', detail.secondaryDx ?? '');
		}
	}, [isEdit, detail, subcodes, setValue]);

	const handleFormSubmit = (values: PatientRegistrationFormValues) => {
		// careTeam = ASSISTING staff only. The primary (index 0 — auto-self on register, the existing
		// primary attending on edit) is managed by the BE and excluded here (PRD 03 §3.3; the BE
		// `careTeam` field excludes the primary). This also avoids resending the viewer as primary.
		const careTeam = assignedStaff.slice(1).map((s) => String(s.id));
		// The seeded/always-present medication row (PRD "최소 1행 유지") is a display convenience —
		// drop rows the clinician left blank so they aren't persisted as empty meds (SHCT-555).
		const medications = (values.medications ?? []).filter(
			(m) => m.name.trim() !== '',
		);
		if (values.appUser === AppUser.PATIENT) {
			const {
				birthDate: _bd,
				gender: _g,
				guardianName: _gn,
				guardianRelationship: _gr,
				guardianPhone: _gp,
				...patientPayload
			} = values;
			onSubmit({ ...patientPayload, medications, careTeam } as PatientRegistrationFormValues);
		} else {
			onSubmit({ ...values, medications, careTeam });
		}
	};

	const addStaff = (member: StaffMember) => {
		if (!assignedStaff.find((s) => s.id === member.id)) {
			setAssignedStaff((prev) => [...prev, member]);
		}
		setShowStaffSearch(false);
		setStaffSearch('');
	};

	const removeStaff = (id: number) => {
		setAssignedStaff((prev) => prev.filter((s) => s.id !== id));
	};

	if (!open) return null;

	const titleKey = isEdit
		? 'medical:patients.edit.title'
		: 'medical:patients.register.title';

	return (
		<div
			ref={modalRef}
			className='modal-overlay modal-overlay--visible'
			role='dialog'
			aria-modal='true'
		>
			<div className='modal modal--lg'>
				{/* ── Header ── */}
				<div className='modal__header'>
					<h3 className='modal__title'>{t(titleKey)}</h3>
					<button
						type='button'
						className='modal__close'
						onClick={onClose}
						aria-label={t('common:actions.close')}
					>
						<X size={20} />
					</button>
				</div>

				{/* ── Body ── */}
				<form onSubmit={handleSubmit(handleFormSubmit)}>
					<div className='modal__body overflow-y-auto max-h-[70vh]'>
						{/* ── App User Type (create only; immutable in edit) ── */}
						{!isEdit && (
							<div className='mb-6 pb-6 border-b border-gray-200'>
								<label className='form-field__label'>
									{t('medical:patients.register.appUserType.label')}{' '}
									<span className='required'>*</span>
								</label>
								<div className='grid grid-cols-2 gap-3'>
									{Object.values(AppUser).map((type) => (
										<label key={type} className='reg-user-type__option'>
											<input
												type='radio'
												value={type}
												{...register('appUser')}
											/>
											<div className='reg-user-type__card'>
												<strong>
													{t(`medical:patients.register.appUserType.${type}`)}
												</strong>
												<small>
													{t(
														`medical:patients.register.appUserType.${type}Desc`,
													)}
												</small>
											</div>
										</label>
									))}
								</div>
							</div>
						)}

						{/* ── Guardian Section (guardian mode only) ── */}
						{appUser === AppUser.GUARDIAN && (
							<div className='reg-section reg-section--guardian'>
								<h4 className='reg-section__title'>
									{t('medical:patients.register.guardianSection.title')}
									<span className='reg-auth-badge'>
										{t('medical:patients.register.guardianSection.badge')}
									</span>
								</h4>
								<p className='reg-section__desc'>
									<Trans
										ns='medical'
										i18nKey='patients.register.guardianSection.desc'
										components={{ strong: <strong /> }}
									/>
								</p>
								<div className='form-grid'>
									<div className='form-field'>
										<label className='form-field__label'>
											{t('medical:patients.register.fields.guardianName')}{' '}
											<span className='required'>*</span>
										</label>
										<input
											type='text'
											className='input'
											placeholder={t(
												'medical:patients.register.fields.placeholder.guardianName',
											)}
											{...register('guardianName')}
										/>
										{(showAll || touchedFields.guardianName) && errors.guardianName && (
											<span className='form-field__error'>
												{t(String(errors.guardianName.message))}
											</span>
										)}
									</div>
									<div className='form-field'>
										<label className='form-field__label'>
											{t(
												'medical:patients.register.fields.guardianRelationship',
											)}{' '}
											<span className='required'>*</span>
										</label>
										<Select
											register={register('guardianRelationship')}
											fullWidth
											options={[
												{ value: '', label: t('common:actions.select') },
												...GUARDIAN_RELATIONSHIPS.map((rel) => ({
													value: rel,
													label: t(
														`medical:patients.register.staff.relationship.${rel}`,
													),
												})),
											]}
										/>
										{(showAll || touchedFields.guardianRelationship) && errors.guardianRelationship && (
											<span className='form-field__error'>
												{t(String(errors.guardianRelationship.message))}
											</span>
										)}
									</div>
									<div className='form-field col-span-full'>
										<label className='form-field__label'>
											{t('medical:patients.register.fields.guardianPhone')}{' '}
											<span className='required'>*</span>{' '}
											<span className='reg-field-hint'>
												(
												{t(
													'medical:patients.register.fields.guardianPhoneHint',
												)}
												)
											</span>
										</label>
										<input
											type='tel'
											className='input'
											placeholder={t(
												'medical:patients.register.fields.placeholder.phone',
											)}
											{...register('guardianPhone')}
										/>
										{(showAll || touchedFields.guardianPhone) && errors.guardianPhone && (
											<span className='form-field__error'>
												{t(String(errors.guardianPhone.message))}
											</span>
										)}
									</div>
								</div>
							</div>
						)}

						{/* ── Patient Info ── */}
						<div className='reg-section'>
							<h4 className='reg-section__title'>
								<User size={18} />
								{t('medical:patients.register.patientSection.title')}
							</h4>
							{appUser === AppUser.PATIENT && (
								<p className='reg-section__desc'>
									<Trans
										ns='medical'
										i18nKey='patients.register.patientSection.patientDesc'
										components={{ strong: <strong /> }}
									/>
								</p>
							)}
							{appUser === AppUser.GUARDIAN && (
								<p className='reg-section__desc'>
									<Trans
										ns='medical'
										i18nKey='patients.register.patientSection.guardianDesc'
										components={{ strong: <strong /> }}
									/>
								</p>
							)}
							<div className='form-grid'>
								<div className='form-field'>
									<label className='form-field__label'>
										{t('medical:patients.register.fields.name')}{' '}
										<span className='required'>*</span>
									</label>
									<input
										type='text'
										className='input'
										placeholder={t(
											'medical:patients.register.fields.placeholder.name',
										)}
										{...register('name')}
									/>
									{(showAll || touchedFields.name) && errors.name && (
										<span className='form-field__error'>
											{t(String(errors.name.message))}
										</span>
									)}
								</div>

								{/* Birth & Gender — guardian mode only (edit: always shown for guardian patients) */}
								{appUser === AppUser.GUARDIAN && (
									<>
										<div className='form-field'>
											<label className='form-field__label'>
												{t('medical:patients.register.fields.birthDate')}{' '}
												<span className='required'>*</span>
											</label>
											<DatePicker register={register('birthDate')} />
											{(showAll || touchedFields.birthDate) && errors.birthDate && (
												<span className='form-field__error'>
													{t(String(errors.birthDate.message))}
												</span>
											)}
										</div>
										<div className='form-field'>
											<label className='form-field__label'>
												{t('medical:patients.register.fields.gender')}{' '}
												<span className='required'>*</span>
											</label>
											<Select
												register={register('gender')}
												fullWidth
												options={[
													{ value: '', label: t('common:actions.select') },
													{
														value: 'M',
														label: t(
															'medical:patients.register.staff.gender.male',
														),
													},
													{
														value: 'F',
														label: t(
															'medical:patients.register.staff.gender.female',
														),
													},
												]}
											/>
											{(showAll || touchedFields.gender) && errors.gender && (
												<span className='form-field__error'>
													{t(String(errors.gender.message))}
												</span>
											)}
										</div>
									</>
								)}

								<div className='form-field'>
									<label className='form-field__label'>
										{t('medical:patients.register.fields.patientPhone')}{' '}
										{appUser === AppUser.PATIENT && (
											<span className='required'>*</span>
										)}
										<span className='reg-field-hint'>
											(
											{t(
												appUser === AppUser.PATIENT
													? 'medical:patients.register.fields.patientPhoneHintPatient'
													: 'medical:patients.register.fields.patientPhoneHint',
											)}
											)
										</span>
									</label>
									<input
										type='tel'
										className='input'
										placeholder={t(
											'medical:patients.register.fields.placeholder.phone',
										)}
										{...register('patientPhone')}
									/>
									{(showAll || touchedFields.patientPhone) && errors.patientPhone && (
										<span className='form-field__error'>
											{t(String(errors.patientPhone.message))}
										</span>
									)}
								</div>

								{/* MRN — create: editable; edit: disabled (immutable, sent as-is to satisfy BE contract TBD_BE_MRN_REQUIRED) */}
								<div className='form-field'>
									<label className='form-field__label'>
										{t('medical:patients.register.fields.mrn')}{' '}
                    <span className='required'>*</span>
									</label>
									<input
										type='text'
										className='input'
										disabled={isEdit}
										placeholder={t(
											'medical:patients.register.fields.placeholder.mrn',
										)}
										{...register('mrn')}
									/>
									{(showAll || touchedFields.mrn) && errors.mrn && (
										<span className='form-field__error'>
											{t(String(errors.mrn.message))}
										</span>
									)}
								</div>

								<div className='form-field form-field--highlight'>
									<label className='form-field__label'>
										⭐ {t('medical:patients.register.fields.dischargeDate')}{' '}
										<span className='required'>*</span>
									</label>
									<DatePicker register={register('dischargeDate')} />
									{(showAll || touchedFields.dischargeDate) && errors.dischargeDate && (
										<span className='form-field__error'>
											{t(String(errors.dischargeDate.message))}
										</span>
									)}
								</div>

								<div className='form-field'>
									<label className='form-field__label'>
										{t('medical:patients.register.fields.primaryDx')}{' '}
										<span className='required'>*</span>
									</label>
									<Select
										register={register('primaryDx')}
										fullWidth
										options={[
											{ value: '', label: t('common:actions.select') },
											...domainOptions.map((d) => ({
												value: String(d.id),
												label: `${d.name} (${d.range})`,
											})),
										]}
									/>
									{(showAll || touchedFields.primaryDx) && errors.primaryDx && (
										<span className='form-field__error'>
											{t(String(errors.primaryDx.message))}
										</span>
									)}
								</div>

								<div className='form-field'>
									<label className='form-field__label'>
										{t('medical:patients.register.fields.secondaryDx')}
									</label>
									<Select
										register={register('secondaryDx')}
										fullWidth
										disabled={!primaryDx}
										options={[
											{
												value: '',
												label: primaryDx
													? t('common:actions.select')
													: t(
															'medical:patients.register.fields.selectPrimaryDxFirst',
														),
											},
											...subcodes.map((sc) => ({
												value: sc.code,
												label: `${sc.code} · ${sc.name}`,
											})),
										]}
									/>
								</div>

								<div className='form-field form-field--full'>
									<label className='form-field__label'>
										{t('medical:patients.register.fields.medication')}{' '}
										<span className='reg-field-hint'>
											({t('medical:patients.register.fields.hint.optional')})
										</span>
									</label>
									{medFields.map((field, i) => (
										<div key={field.id} className='mb-3'>
											<div className='flex items-center gap-2 mb-1'>
												<div className='flex-[2]'>
													<input
														type='text'
														className='input'
														placeholder={t(
															'medical:patients.register.fields.placeholder.medication',
														)}
														{...register(`medications.${i}.name`)}
													/>
													{(showAll || touchedFields.medications?.[i]?.name) && errors.medications?.[i]?.name && (
														<span className='form-field__error'>
															{t(String(errors.medications[i].name.message))}
														</span>
													)}
												</div>
												<div className='flex-1'>
													<input
														type='text'
														className='input'
														placeholder={t(
															'medical:patients.register.fields.placeholder.medicationFreq',
														)}
														{...register(`medications.${i}.freq`)}
													/>
													{(showAll || touchedFields.medications?.[i]?.freq) && errors.medications?.[i]?.freq && (
														<span className='form-field__error'>
															{t(String(errors.medications[i].freq.message))}
														</span>
													)}
												</div>
												<button
													type='button'
													className='shrink-0 w-8 h-8 border border-gray-200 bg-white rounded-md cursor-pointer text-gray-500 flex items-center justify-center'
													aria-label={t('common:actions.delete')}
													onClick={() => medRemove(i)}
												>
													<X size={14} />
												</button>
											</div>
										</div>
									))}
									<button
										type='button'
										className='btn btn--secondary btn--sm mt-2 justify-center'
										onClick={() => medAppend({ name: '', freq: '' })}
									>
										<Plus size={16} /> {t('medical:patients.register.fields.addMedication')}
									</button>
								</div>
							</div>
						</div>

						{/* ── Staff Assignment ── */}
						<div className='reg-section'>
							<h4 className='reg-section__title'>
								<Stethoscope size={18} />
								{t('medical:patients.register.staff.title')}
							</h4>
							<div className='form-field form-field--full mb-0'>
								<div className='staff-chips'>
									{assignedStaff.map((s, i) => {
										const isPrimary = i === 0;
										return (
											<span
												key={s.id}
												className={`chip${isPrimary ? ' chip--primary' : ''}`}
											>
												<span
													className={`badge badge--xs ${isPrimary ? 'badge--blue' : 'badge--gray'}`}
												>
													{isPrimary
														? t('medical:patients.register.staff.primary')
														: t('medical:patients.register.staff.assistant')}
												</span>
												{s.name} ({s.department})
												{!isPrimary && (
													<button
														type='button'
														className='chip__remove'
														onClick={() => removeStaff(s.id)}
														aria-label={t('common:actions.delete')}
													>
														<X size={14} />
													</button>
												)}
											</span>
										);
									})}
								</div>
								<button
									type='button'
									className='btn btn--secondary btn--sm justify-center mt-2'
									onClick={() => setShowStaffSearch(true)}
								>
									<Plus size={16} /> {t('medical:patients.register.staff.add')}
								</button>
								<div className='form-field__hint'>
									{t('medical:patients.register.staff.hint')}
								</div>
							</div>
						</div>
					</div>

					{/* ── Footer ──
					   Prototype uses the BEM btn--secondary / btn--primary classes (patients.html
					   #regModal footer), matching the modal's own inline buttons. The shared <Button>
					   component emits single-dash btn-primary (theme --primary #030213) which renders a
					   different tone than the slate #374151 the prototype specifies, so use the raw
					   prototype classes here (SHCT-539). */}
					<div className='modal__footer'>
						<button type='button' className='btn btn--secondary' onClick={onClose}>
							{t('common:actions.cancel')}
						</button>
						<button
							type='submit'
							className='btn btn--primary'
							disabled={isSubmitting || !isValid}
						>
							{isEdit ? t('common:actions.edit') : t('common:actions.register')}
						</button>
					</div>
				</form>
			</div>

			{/* ── Staff Search Sub-Modal ── */}
			{showStaffSearch && (
				<StaffSearchModal
					staff={staffMembers}
					assigned={assignedStaff}
					search={staffSearch}
					onSearchChange={setStaffSearch}
					onAdd={addStaff}
					onClose={() => setShowStaffSearch(false)}
				/>
			)}
		</div>
	);
}
